import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  X,
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Wallet as WalletIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import { useWeb3 } from "../contexts/Web3Context";

const AuthModalWidget = ({
  isOpen,
  onClose,
  /** Hides modal before wallet popup without side effects (e.g. skip navigate on /login) */
  onHideForWalletConnect,
  initialMode = "login",
  onWalletConnected,
  referralCode: propReferralCode,
}) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(initialMode === "login");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signupMethod, setSignupMethod] = useState("wallet"); // 'wallet' or 'email'
  const [pendingWalletConnection, setPendingWalletConnection] = useState(false);
  const walletLoginInFlightRef = useRef(false);

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm();

  const { connectWallet, isConnected, walletAddress } = useWeb3();
  const [searchParams] = useSearchParams();

  // Get referral code from URL params or prop
  const urlReferralCode = searchParams.get('ref');
  const referralCode = propReferralCode || urlReferralCode || '';

  const hideModalForWalletPopup = () => {
    if (typeof onHideForWalletConnect === "function") {
      onHideForWalletConnect();
    } else {
      onClose();
    }
  };

  const handleWalletLogin = async (address) => {
    if (!address || walletLoginInFlightRef.current) return;
    walletLoginInFlightRef.current = true;
    setIsLoading(true);
    try {
      const result = await login({ identifier: address });
      if (result.success) {
        toast.success("Login successful!");
        setPendingWalletConnection(false);
        onClose();
        reset();
      } else {
        toast.error(result.error || "Login failed. Please check your wallet address.");
        setPendingWalletConnection(false);
      }
    } catch (error) {
      toast.error("Login failed. Please try again.");
      setPendingWalletConnection(false);
    } finally {
      setIsLoading(false);
      walletLoginInFlightRef.current = false;
    }
  };

  // After wallet connects: login automatically, or reopen modal for signup
  useEffect(() => {
    if (!isConnected || !walletAddress || !pendingWalletConnection) return;

    if (isLogin) {
      handleWalletLogin(walletAddress);
      return;
    }

    setPendingWalletConnection(false);
    setValue("walletAddress", walletAddress, { shouldValidate: true });
    toast.success("Wallet connected! Please complete your registration.");
    if (onWalletConnected) {
      setTimeout(() => {
        onWalletConnected();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, walletAddress, pendingWalletConnection, isLogin, setValue]);

  const handleConnectWallet = async () => {
    // Close auth modal first so the wallet provider popup is visible
    hideModalForWalletPopup();
    setPendingWalletConnection(true);

    try {
      const res = await connectWallet();
      if (!res || !res.address) {
        setPendingWalletConnection(false);
        toast.error("Wallet connection cancelled or failed.");
        return;
      }
      // Login/signup completion is handled by the useEffect above when connection state updates
    } catch (err) {
      console.error("wallet connect error", err);
      toast.error("Wallet connection failed. Please try again.");
      setPendingWalletConnection(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      // Validate email signup requires password and confirmPassword
      if (!isLogin && signupMethod === "email") {
        if (!data.password || !data.confirmPassword) {
          toast.error("Password and confirm password are required for email registration.");
          setIsLoading(false);
          return;
        }
        if (data.password !== data.confirmPassword) {
          toast.error("Passwords do not match.");
          setIsLoading(false);
          return;
        }
      }

      // Validate wallet signup requires wallet address
      if (!isLogin && signupMethod === "wallet") {
        if (!data.walletAddress) {
          toast.error("Please connect your wallet to continue.");
          setIsLoading(false);
          return;
        }
      }

      // Prepare registration data based on signup method
      let registrationData = {
        username: data.username?.trim(),
      };

      // Add referral code if provided (works for both methods)
      if (data.referredBy?.trim()) {
        registrationData.referredBy = data.referredBy.trim();
      }

      // For email signup: include email and password
      if (signupMethod === "email") {
        if (data.email?.trim()) {
          registrationData.email = data.email.trim();
        }
        if (data.password?.trim()) {
          registrationData.password = data.password.trim();
        }
        // Explicitly do NOT include walletAddress
      }

      // For wallet signup: include walletAddress only
      if (signupMethod === "wallet") {
        if (data.walletAddress?.trim()) {
          registrationData.walletAddress = data.walletAddress.trim();
        }
        // Explicitly do NOT include email or password
      }

      // Log the registration data being sent
      console.log('=== FRONTEND: Registration ===');
      console.log('Signup method:', signupMethod);
      console.log('Registration data:', JSON.stringify(registrationData, null, 2));

      const result = isLogin
        ? await login({ 
            identifier: data.identifier?.trim(), 
            password: data.password?.trim() || undefined 
          })
        : await register(registrationData);

      if (result.success) {
        toast.success(isLogin ? "Login successful!" : "Registration successful!");
        onClose();
        reset();
        // Redirect to dashboard after registration
        if (!isLogin) {
          setTimeout(() => {
            navigate('/dashboard');
          }, 300);
        }
      }
    } catch (error) {
      // Error is already handled by AuthContext with toast
      console.error("Authentication error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    reset();
    setIsLoading(false);
    setPendingWalletConnection(false);
  };

  const closeModal = () => {
    onClose();
    reset();
    setIsLoading(false);
    setPendingWalletConnection(false);
  };

  // Update isLogin when initialMode changes
  useEffect(() => {
    setIsLogin(initialMode === "login");
  }, [initialMode]);

  // when switching into signup mode default to wallet method
  useEffect(() => {
    if (!isLogin) {
      setSignupMethod("wallet");
    }
  }, [isLogin]);

  // When modal opens in signup mode, ensure wallet method is selected if wallet is connected
  useEffect(() => {
    if (isOpen && !isLogin && isConnected && walletAddress) {
      setSignupMethod("wallet");
    }
  }, [isOpen, isLogin, isConnected, walletAddress]);

  // If wallet is already connected and we're in signup mode, populate the field
  // This runs when modal opens or when wallet connects
  useEffect(() => {
    if (isOpen && !isLogin && signupMethod === "wallet") {
      if (walletAddress) {
        // Update the form value when wallet address is available
        setValue("walletAddress", walletAddress, { shouldValidate: true });
      } else {
        // Clear the form value if wallet is disconnected
        setValue("walletAddress", "", { shouldValidate: false });
      }
    }
  }, [isOpen, isLogin, signupMethod, walletAddress, setValue]);

  // Pre-fill referral code when modal opens or referral code changes
  useEffect(() => {
    if (isOpen && !isLogin) {
      if (referralCode) {
        setValue("referredBy", referralCode, { shouldValidate: false });
      } else {
        // Clear referral code if not provided
        setValue("referredBy", "", { shouldValidate: false });
      }
    }
  }, [isOpen, isLogin, referralCode, setValue]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <>
      {/* Modal */}
      {isOpen && (
        <div className="modal-overlay" style={{ zIndex: 40 }}>
          <div className="modal-content" style={{ zIndex: 50 }}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-heading text-heading dark:text-white">
                {isLogin ? "Sign In" : "Create Account"}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              {/* Signup method toggle (only on signup) */}
              {!isLogin && (
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setSignupMethod("wallet")}
                    className={`px-3 py-1 rounded-full border ${
                      signupMethod === "wallet"
                        ? "bg-accent text-white border-accent"
                        : "bg-transparent"
                    } text-sm`}
                  >
                    Wallet
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignupMethod("email")}
                    className={`px-3 py-1 rounded-full border ${
                      signupMethod === "email"
                        ? "bg-accent text-white border-accent"
                        : "bg-transparent"
                    } text-sm`}
                  >
                    Email
                  </button>
                </div>
              )}
              
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Username *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                    <input
                      type="text"
                      {...registerField("username", {
                        required: "Username is required",
                        minLength: {
                          value: 3,
                          message: "Username must be at least 3 characters",
                        },
                      })}
                      className="input-field input-field-icon"
                      placeholder="Choose a username"
                    />
                  </div>
                  {errors.username && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.username.message}
                    </p>
                  )}
                </div>
              )}

              {/* Identifier (login) - can be email or wallet address */}
              {isLogin ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Enter your Email or username
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                    <input
                      type="text"
                      {...registerField("identifier", {
                        required: "Email or username is required",
                      })}
                      className="input-field input-field-icon"
                      placeholder="email@example.com or username"
                      autoComplete="username"
                    />
                  </div>
                  {errors.identifier && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.identifier.message}
                    </p>
                  )}
                </div>
              ) : (
                signupMethod === "email" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                      <input
                        type="email"
                        {...registerField("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        })}
                        className="input-field input-field-icon"
                        placeholder="Enter email address"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                )
              )}

              {/* Wallet address block only for wallet signup */}
              {!isLogin && signupMethod === "wallet" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Wallet Address *
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={handleConnectWallet}
                      disabled={isLoading || pendingWalletConnection}
                      className="btn-wallet flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium hover:shadow-md transition-shadow duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <WalletIcon className="w-4 h-4 text-gray-700 dark:text-gray-200" />
                      <span>{walletAddress ? "Reconnect Wallet" : "Connect Wallet"}</span>
                    </button>

                    {/* Visible, read-only input that displays and stores the connected wallet address */}
                    <input
                      type="text"
                      {...registerField("walletAddress", {
                        required: signupMethod === "wallet" ? "Wallet address is required" : false,
                        validate: (value) => {
                          if (signupMethod === "wallet" && !value && !walletAddress) {
                            return "Please connect your wallet";
                          }
                          return true;
                        },
                      })}
                      readOnly
                      className="input-field pl-3 flex-1"
                      placeholder={walletAddress ? walletAddress : "Your wallet address will appear here"}
                    />
                  </div>
                  {errors.walletAddress && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.walletAddress.message}
                    </p>
                  )}
                  {!walletAddress && (
                    <p className="text-gray-500 text-xs mt-1">
                      Click "Connect Wallet" to connect your wallet
                    </p>
                  )}
                </div>
              )}

              {/* Password field for login or email signup */}
              {(isLogin || (!isLogin && signupMethod === "email")) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password {!isLogin && signupMethod === "email" && "*"}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                    <input
                      type={showPassword ? "text" : "password"}
                      {...registerField("password", signupMethod === "email" ? {
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters",
                        },
                      } : {})}
                      className="input-field input-field-icon input-field-icon-right"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>
              )}

              {/* Confirm password for email signup */}
              {!isLogin && signupMethod === "email" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                    <input
                      type={showPassword ? "text" : "password"}
                      {...registerField("confirmPassword", {
                        required: "Please confirm your password",
                        validate: (value) => {
                          const password = watch("password");
                          if (value !== password) {
                            return "Passwords do not match";
                          }
                          return true;
                        },
                      })}
                      className="input-field input-field-icon input-field-icon-right"
                      placeholder="Repeat password"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              )}

              {/* Referral Code */}
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Referral Code {referralCode && <span className="text-green-600 dark:text-green-400">(Pre-filled)</span>}
                  </label>
                  <input
                    type="text"
                    {...registerField("referredBy")}
                    className="input-field"
                    placeholder="Enter referral code"
                    readOnly={!!referralCode}
                    style={referralCode ? { 
                      backgroundColor: 'rgba(34, 197, 94, 0.1)', 
                      cursor: 'not-allowed',
                      borderColor: 'rgba(34, 197, 94, 0.5)'
                    } : {}}
                  />
                  {referralCode && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center">
                      <span className="mr-1">✓</span>
                      Referral code detected from link. The referrer will earn rewards when you complete your first job or gig!
                    </p>
                  )}
                </div>
              )}

              {isLogin && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-accent focus:ring-accent"
                    />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      Remember me
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-sm text-accent hover:text-accent/80"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || pendingWalletConnection}
                className="w-full btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="spinner mr-2" />
                    {isLogin ? "Signing In..." : "Creating Account..."}
                  </>
                ) : isLogin ? (
                  "Sign In"
                ) : (
                  "Create Account"
                )}
              </button>
              
              {/* Connect wallet quick action for login */}
              {isLogin && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleConnectWallet}
                    disabled={isLoading || pendingWalletConnection}
                    className="w-full btn-wallet flex items-center justify-center space-x-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium hover:shadow-md transition-shadow duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <WalletIcon className="w-4 h-4 text-gray-700 dark:text-gray-200" />
                    <span>Connect Wallet to Sign In</span>
                  </button>
                </div>
              )}
            </form>

            {/* Footer */}
            <div className="px-6 pb-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {isLogin
                    ? "Don't have an account?"
                    : "Already have an account?"}
                  <button
                    onClick={switchMode}
                    className="text-accent hover:text-accent/80 ml-1 font-medium"
                  >
                    {isLogin ? "Sign up" : "Sign in"}
                  </button>
                </p>
              </div>

              <div className="mt-4 text-xs text-gray-500 dark:text-gray-500 text-center">
                By {isLogin ? "signing in" : "creating an account"}, you agree
                to our{" "}
                <a href="/terms" className="text-accent hover:text-accent/80">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy" className="text-accent hover:text-accent/80">
                  Privacy Policy
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthModalWidget;
